/**
 * Planning Capacity Query Hooks (Epic D-1 — Fundament, Story D-1.3)
 *
 * Eigen bestand, zelfde query-keys-factory + invalidatie-patroon als
 * `useQualificationLists.ts` (AD-9/AD-18): elke mutatie invalideert de
 * bijbehorende query, geen inline `apiRequest` in componenten.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { planningCapacityApi } from '../../../lib/planningCapacityApi';
import type { PlanningTaskActivity } from '@functieloonhuis/shared';

export const planningCapacityKeys = {
  all: ['planningCapacity'] as const,
  list: () => [...planningCapacityKeys.all, 'list'] as const,
  weights: () => [...planningCapacityKeys.all, 'weights'] as const,
};

/** FR-6 — het dagcijfer per medewerker, voor élke medewerker (default 12 al ingevuld). */
export function useCapacityList() {
  return useQuery({
    queryKey: planningCapacityKeys.list(),
    queryFn: () => planningCapacityApi.listCapacity(),
  });
}

export function useUpdateDailyScore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, dailyScore }: { employeeId: number; dailyScore: number }) =>
      planningCapacityApi.updateDailyScore(employeeId, dailyScore),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningCapacityKeys.list() });
    },
  });
}

/** FR-7 — altijd alle zes activiteiten, in de volgorde van `PLANNING_TASK_ACTIVITIES`. */
export function useActivityWeights() {
  return useQuery({
    queryKey: planningCapacityKeys.weights(),
    queryFn: () => planningCapacityApi.listActivityWeights(),
  });
}

export function useUpdateActivityWeight() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ activity, weight }: { activity: PlanningTaskActivity; weight: number }) =>
      planningCapacityApi.updateActivityWeight(activity, weight),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planningCapacityKeys.weights() });
    },
  });
}
